import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
  Link,
  Divider,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import type { Brand } from '../../types/brand';
import { categoriesApi, CategoryBrand } from '../../api/categories';
import { categoryProductsApi } from '../../api/categoryProducts';

interface BrandFilterSidebarProps {
  brands?: Brand[];
  selectedBrands: number[];
  selectedSubcategories?: number[];
  onBrandChange: (brandIds: number[]) => void;
  isMobile?: boolean;
  categoryId?: number;
}

const BrandFilterSidebar: React.FC<BrandFilterSidebarProps> = ({
  brands: fallbackBrands = [],
  selectedBrands,
  selectedSubcategories = [],
  onBrandChange,
  isMobile = false,
  categoryId,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [categoryBrands, setCategoryBrands] = useState<CategoryBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [brandCounts, setBrandCounts] = useState<Map<number, number>>(new Map());

  // Fetch brands from API when categoryId is provided
  useEffect(() => {
    const fetchCategoryBrands = async () => {
      if (!categoryId) {
        setCategoryBrands([]);
        return;
      }
      
      setLoading(true);
      try {
        const brands = await categoriesApi.getCategoryBrands(categoryId);
        // Ensure we always set an array
        setCategoryBrands(Array.isArray(brands) ? brands : []);
      } catch (error) {
        console.error('Error fetching category brands:', error);
        setCategoryBrands([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryBrands();
  }, [categoryId]);

  // Fetch counts from API for each brand
  useEffect(() => {
    const fetchBrandCounts = async () => {
      if (!categoryId) return;

      // Use category brands from API if available, otherwise fallback to filtered brands
      const sourceBrands = Array.isArray(categoryBrands) && categoryBrands.length > 0 
        ? categoryBrands 
        : (Array.isArray(fallbackBrands) ? fallbackBrands : []);

      if (!Array.isArray(sourceBrands) || sourceBrands.length === 0) {
        return;
      }

      const countsMap = new Map<number, number>();

      // Fetch count for each brand with categoryId + brandId + optional subcategories
      await Promise.all(
        sourceBrands
          .filter(brand => brand && brand.isActive !== false)
          .map(async (brand) => {
            try {
              const params = {
                categoryIds: selectedSubcategories.length > 0 
                  ? selectedSubcategories 
                  : [categoryId], // Default: main categoryId
                brandIds: [brand.id],
              };
              
              const result = await categoryProductsApi.getProductCount(params);
              countsMap.set(brand.id, result.count);
            } catch (error) {
              console.error(`Error fetching count for brand ${brand.id}:`, error);
              countsMap.set(brand.id, 0);
            }
          })
      );

      setBrandCounts(countsMap);
    };

    fetchBrandCounts();
  }, [categoryBrands, fallbackBrands, selectedSubcategories, categoryId]);

  // Get brands with counts - use API brands if available, otherwise fallback to filtered brands
  const brandsWithCounts = useMemo(() => {
    // Use category brands from API if available, otherwise fallback to filtered brands
    // Ensure we always have an array
    const sourceBrands = Array.isArray(categoryBrands) && categoryBrands.length > 0 
      ? categoryBrands 
      : (Array.isArray(fallbackBrands) ? fallbackBrands : []);

    if (!Array.isArray(sourceBrands)) {
      return [];
    }

    const brandsWithCountsArray = sourceBrands
      .filter(brand => brand && brand.isActive !== false)
      .map(brand => ({
        ...brand,
        count: brandCounts.get(brand.id) || 0,
      }));

    // Sort alphabetically by name
    return brandsWithCountsArray.sort((a, b) => a.name.localeCompare(b.name));
  }, [categoryBrands, fallbackBrands, brandCounts]);

  const visibleBrands = showAll ? brandsWithCounts : brandsWithCounts.slice(0, 8);

  const handleBrandToggle = (brandId: number) => {
    if (selectedBrands.includes(brandId)) {
      onBrandChange(selectedBrands.filter(id => id !== brandId));
    } else {
      onBrandChange([...selectedBrands, brandId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedBrands.length === brandsWithCounts.length) {
      onBrandChange([]);
    } else {
      onBrandChange(brandsWithCounts.map(brand => brand.id));
    }
  };

  // Don't render if no brands or still loading
  if (brandsWithCounts.length === 0 && !loading) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: 'white',
        borderRadius: isMobile ? 0 : 2,
        p: 3,
        height: 'fit-content',
        ...(isMobile ? {} : { position: 'sticky', top: 100 }),
        border: '1px solid #e0e0e0',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            color: '#333',
          }}
        >
          {t('product.brand')}
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {/* Divider */}
        <Divider sx={{ mb: 2 }} />
        
        {/* Select All */}
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedBrands.length === brandsWithCounts.length && brandsWithCounts.length > 0}
              indeterminate={selectedBrands.length > 0 && selectedBrands.length < brandsWithCounts.length}
              onChange={handleSelectAll}
              sx={{
                color: '#b48b6a',
                '&.Mui-checked': {
                  color: '#b48b6a',
                },
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
              {t('category.allBrands')}
            </Typography>
          }
          sx={{ mb: 1 }}
        />

        {/* Brand List */}
        <Box sx={{ mt: 1 }}>
          {visibleBrands.map((brand) => (
            <FormControlLabel
              key={brand.id}
              control={
                <Checkbox
                  checked={selectedBrands.includes(brand.id)}
                  onChange={() => handleBrandToggle(brand.id)}
                  sx={{
                    color: '#b48b6a',
                    '&.Mui-checked': {
                      color: '#b48b6a',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    {brand.name}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#666', ml: 1 }}>
                    ({brand.count})
                  </Typography>
                </Box>
              }
              sx={{ 
                width: '100%',
                mb: 0.5,
                '& .MuiFormControlLabel-label': {
                  width: '100%',
                },
              }}
            />
          ))}

          {/* Show More/Less */}
          {brandsWithCounts.length > 8 && (
            <Box sx={{ mt: 1 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setShowAll(!showAll)}
                sx={{
                  color: '#b48b6a',
                  textDecoration: 'none',
                  fontSize: '13px',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {showAll ? t('category.showLess') : `${t('category.showMore')} (${brandsWithCounts.length - 8})`}
              </Link>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default BrandFilterSidebar;
